import React, { useState } from 'react';
import {
  Tabs,
  Tab,
  Box,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  CircularProgress,
  Button,
  Typography,
  Menu,
  MenuItem,
  ListItemText,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Chip
} from '@mui/material';
import MainCard from 'components/MainCard';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import Lottie from 'react-lottie';
import lottieLoader from '../../assets/Lottie/NoLoaderData.json';
import Filter from '../../components/reports/Filter';
import { getReport } from 'services/report/getReport';
import { getPranayamaReport } from 'services/pranayamaReport/getPranayamaReport';
import { getRelaxationReport } from '../../services/relaxationReport/getRelaxationReport'; // Added Service
import { exportToCSV, exportToPDF, generateFilename } from '../../utils/exportUtils';
import { sendFeedback } from 'services/feedback/sendFeedBack';
import toast from 'react-hot-toast';
import AsanaPreview from 'components/AsanaImageWithLandmarks';
import { generateImageWithLandmarks } from 'utils/drawAsanaImage';

const loaderOptions = {
  loop: true,
  autoplay: true,
  animationData: lottieLoader,
  rendererSettings: {
    preserveAspectRatio: 'xMidYMid slice'
  }
};

function FeedbackDialogs({
  openFeedbackDialog,
  currentFeedbackRow,
  feedbackText,
  setFeedbackText,
  handleCloseFeedbackDialog,
  handleSubmitFeedback,
  openViewFeedbackDialog,
  viewFeedbackText,
  handleCloseViewFeedbackDialog
}) {
  return (
    <>
      <Dialog open={openFeedbackDialog} onClose={handleCloseFeedbackDialog} maxWidth="sm" fullWidth>
        <DialogTitle>
          Give Feedback for {currentFeedbackRow?.studentName || currentFeedbackRow?.student_name} -{' '}
          {currentFeedbackRow?.asanaName}
        </DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            margin="dense"
            label="Feedback"
            type="text"
            fullWidth
            multiline
            minRows={3}
            value={feedbackText}
            onChange={(e) => setFeedbackText(e.target.value)}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseFeedbackDialog}>Cancel</Button>
          <Button onClick={handleSubmitFeedback} variant="contained" disabled={!feedbackText.trim()}>
            Submit
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={openViewFeedbackDialog} onClose={handleCloseViewFeedbackDialog} maxWidth="sm" fullWidth>
        <DialogTitle>Feedback</DialogTitle>
        <DialogContent>
          <Typography variant="body1" sx={{ whiteSpace: 'pre-line' }}>
            {viewFeedbackText || 'No feedback provided'}
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseViewFeedbackDialog}>Close</Button>
        </DialogActions>
      </Dialog>
    </>
  );
}

export default function StudentReports() {
  const coachId = localStorage.getItem('userid');
    const studentId = localStorage.getItem('studentid');

  const queryClient = useQueryClient();

  const [tabValue, setTabValue] = useState(0);

  // Asana Reports
  const currentRoleId = localStorage.getItem('roleid');
  const { data: asanaData, isLoading: asanaLoading } = useQuery({
    enabled: !!coachId && tabValue === 0,
    queryKey: ['asana-reports', coachId, currentRoleId],
    queryFn: () => getReport({ 
      coachId: Number(coachId), 
      roleid: currentRoleId === '4' ? 5 : undefined 
    })
  });
  const asanaReports = asanaData?.data || [];
  const sortedAsanaReports = asanaReports.slice().sort((a, b) => new Date(b.date) - new Date(a.date));

  // Pranayama Reports
  const { data: pranayamaData, isLoading: pranayamaLoading } = useQuery({
    enabled: !!coachId && tabValue === 1,
    queryKey: ['pranayama-reports', coachId, currentRoleId],
    queryFn: () => getPranayamaReport({ 
      coachId: Number(coachId),
      roleid: currentRoleId === '4' ? 5 : undefined
    })
  });
  const pranayamaReports = pranayamaData?.response || [];
  const sortedPranayamaReports = pranayamaReports.slice().sort((a, b) => new Date(b.date) - new Date(a.date));

  // Relaxation Reports (New Integration)
  const { data: relaxationData, isLoading: relaxationLoading } = useQuery({
    enabled: !!coachId && tabValue === 2,
    queryKey: ['relaxation-reports', coachId, currentRoleId],
    queryFn: () => getRelaxationReport({ 
      coachId: Number(coachId),
      roleid: currentRoleId === '4' ? 5 : undefined
    })
  });
  const relaxationReports = relaxationData?.response || [];
  console.log("😂",relaxationReports)

  // Shared filter & export state
  const [selectedFilters, setSelectedFilters] = useState([]);
  const [filterValues, setFilterValues] = useState({
    'Student Name': '',
    'Session': '',
    'Practice': '',
    'Date': [null, null]
  });
  const [exportAnchorEl, setExportAnchorEl] = useState(null);
  const [isExporting, setIsExporting] = useState(false);

  // Feedback state
  const [openFeedbackDialog, setOpenFeedbackDialog] = useState(false);
  const [currentFeedbackRow, setCurrentFeedbackRow] = useState(null);
  const [feedbackText, setFeedbackText] = useState('');
  const [openViewFeedbackDialog, setOpenViewFeedbackDialog] = useState(false);
  const [viewFeedbackText, setViewFeedbackText] = useState('');

  const sendFeedbackMutation = useMutation({
    mutationFn: ({ reportId, message }) => sendFeedback({ reportId, message }),
    onSuccess: () => {
      toast.success('Feedback sent!');
      queryClient.invalidateQueries(['asana-reports', coachId]);
    },
    onError: (error) => {
      toast.error(error?.response?.data?.message || 'Error sending feedback');
    }
  });

  const handleTabChange = (event, newValue) => {
    setTabValue(newValue);
    setSelectedFilters([]);
    setFilterValues({
      'Student Name': '',
      'Session': '',
      'Practice': '',
      'Date': [null, null]
    });
  };

  const handleFilterChange = (event) => {
    const value = event.target.value;
    setSelectedFilters(typeof value === 'string' ? value.split(',') : value);
  };

  const handleFilterValueChange = (filterName, value) => {
    setFilterValues((prev) => ({
      ...prev,
      [filterName]: value
    }));
  };

  const handleDateChange = (index, newValue) => {
    setFilterValues((prev) => ({
      ...prev,
      Date: prev['Date'].map((d, i) => (i === index ? newValue : d))
    }));
  };

  const handleExportClick = (event) => {
    setExportAnchorEl(event.currentTarget);
  };

  const handleExportClose = () => {
    setExportAnchorEl(null);
  };

  const handleExport = async (format) => {
    setIsExporting(true);
    try {
      const dataToExport = filteredReports.length > 0 ? filteredReports : [];
      let type = 'asana-reports';
      if (tabValue === 1) type = 'pranayama-reports';
      if (tabValue === 2) type = 'relaxation-reports';
      
      const filename = generateFilename(type, format);
      if (format === 'csv') {
        exportToCSV(dataToExport, filename);
      } else if (format === 'pdf') {
        await exportToPDF(dataToExport, filename);
      }
    } catch (error) {
      console.error('Export failed:', error);
      toast.error('Failed to export data');
    } finally {
      setIsExporting(false);
      handleExportClose();
    }
  };

  const handleOpenFeedbackDialog = (row) => {
    setCurrentFeedbackRow(row);
    setFeedbackText('');
    setOpenFeedbackDialog(true);
  };

  const handleCloseFeedbackDialog = () => {
    setOpenFeedbackDialog(false);
    setCurrentFeedbackRow(null);
    setFeedbackText('');
  };

  const handleSubmitFeedback = () => {
    if (currentFeedbackRow && feedbackText.trim()) {
      sendFeedbackMutation.mutate({
        reportId: currentFeedbackRow.reportId,
        message: feedbackText
      });
    }
    handleCloseFeedbackDialog();
  };

  const handleViewFeedback = (feedback) => {
    setViewFeedbackText(feedback || 'No feedback provided');
    setOpenViewFeedbackDialog(true);
  };

  const handleCloseViewFeedbackDialog = () => {
    setOpenViewFeedbackDialog(false);
    setViewFeedbackText('');
  };

  const handleImageClick = async (report) => {
    try {
      const imageUrl = report.asanaimage;
      if (!imageUrl) {
        toast.error('No image available');
        return;
      }
      let landmarks = [];
      if (report.asanalandmarks) {
        try { landmarks = JSON.parse(report.asanalandmarks); } catch (e) { console.error('Invalid landmarks:', e); }
      }
      const imageWithLandmarks = await generateImageWithLandmarks(imageUrl, landmarks, true);
      const newTab = window.open('', '_blank');
      if (newTab) {
        newTab.document.write(`
          <html>
            <head><title>${report.asanaName} Image</title></head>
            <body style="margin:0;display:flex;align-items:center;justify-content:center;height:100vh;background:#000;">
              <img src="${imageWithLandmarks}" style="max-width:100%;max-height:100%;object-fit:contain;" />
            </body>
          </html>
        `);
        newTab.document.close();
      }
    } catch (error) {
      console.error('Image preview failed:', error);
      toast.error('Unable to open image');
    }
  };

  function getUniqueValues(arr, key) {
    return [...new Set(arr.map(item => item[key]).filter(Boolean))];
  }

  const currentReports = tabValue === 0 ? sortedAsanaReports : tabValue === 1 ? sortedPranayamaReports : relaxationReports;
  const isLoadingCurrent = tabValue === 0 ? asanaLoading : tabValue === 1 ? pranayamaLoading : relaxationLoading;

  const filterOptions = {
    'Student Name': getUniqueValues(currentReports, tabValue === 2 ? 'studentName' : ('studentName' || 'student_name')),
    'Session': getUniqueValues(currentReports, 'courseName'),
    'Practice': getUniqueValues(currentReports, tabValue === 0 ? 'asanaName' : tabValue === 1 ? 'pranayamaName' : 'relaxationName'),
    'Date': []
  };

  const filteredReports = currentReports.filter((report) => {
    return selectedFilters.every((filter) => {
      if (filter === 'Date') {
        const [start, end] = filterValues['Date'];
        if (start && end) {
          const reportDateStr = report.date || report.createdAt;
          const reportDate = new Date(reportDateStr).toISOString().split('T')[0];
          return reportDate >= start.format('YYYY-MM-DD') && reportDate <= end.format('YYYY-MM-DD');
        }
        return true;
      }
      if (!filterValues[filter]) return true;
      const practiceKey = tabValue === 0 ? 'asanaName' : tabValue === 1 ? 'pranayamaName' : 'relaxationName';
      const studentKey = tabValue === 2 ? 'studentName' : (report.studentName ? 'studentName' : 'student_name');
      const keyMap = { 'Practice': practiceKey, 'Student Name': studentKey, 'Session': 'courseName' };
      return report[keyMap[filter]] === filterValues[filter];
    });
  });

  return (
    <MainCard>
      <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 3 }}>
        <Tabs value={tabValue} onChange={handleTabChange}>
          <Tab label="Asana Reports" />
          <Tab label="Pranayama Reports" />
          <Tab label="Relaxation Reports" />
        </Tabs>
      </Box>

      <Box sx={{ display: 'flex', flexDirection: 'row', justifyContent: 'space-between', alignItems: 'center', py: 2 }}>
        <Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
          <Typography>Filter By :</Typography>
          <Filter
            selectedFilters={selectedFilters}
            handleFilterChange={handleFilterChange}
            filterValues={filterValues}
            handleFilterValueChange={handleFilterValueChange}
            handleDateChange={handleDateChange}
            filterOptions={filterOptions}
          />
        </Box>

        {filteredReports.length > 0 && (
          <Box>
            <Button
              variant="contained"
              color="primary"
              onClick={handleExportClick}
              disabled={isExporting}
              startIcon={isExporting ? <CircularProgress size={16} /> : null}
            >
              {isExporting ? 'Exporting...' : 'Export'}
            </Button>
            <Menu
              anchorEl={exportAnchorEl}
              open={Boolean(exportAnchorEl)}
              onClose={handleExportClose}
              anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
              transformOrigin={{ vertical: 'top', horizontal: 'right' }}
            >
              <MenuItem onClick={() => handleExport('csv')}>
                <ListItemText>Export as CSV</ListItemText>
              </MenuItem>
              <MenuItem onClick={() => handleExport('pdf')}>
                <ListItemText>Export as PDF</ListItemText>
              </MenuItem>
            </Menu>
          </Box>
        )}
      </Box>

      <TableContainer component={Paper}>
        <Table sx={{ minWidth: 800 }} aria-label="reports table">
          <TableHead>
            <TableRow>
              <TableCell>#</TableCell>
              <TableCell>Student Name</TableCell>
              <TableCell>Course</TableCell>
              <TableCell>
                {tabValue === 0 ? 'Asana' : tabValue === 1 ? 'Pranayama' : 'Relaxation Session'}
              </TableCell>
              <TableCell>Date</TableCell>

              {tabValue === 0 && (
                <>
                  <TableCell>Performance (%)</TableCell>
                  <TableCell>Image</TableCell>
                  <TableCell>Feedback</TableCell>
                </>
              )}

              {tabValue === 1 && (
                <>
                  <TableCell>Duration (s)</TableCell>
                  <TableCell>Loops</TableCell>
                  <TableCell>Interruptions</TableCell>
                </>
              )}

              {tabValue === 2 && (
                <TableCell align="right">Status</TableCell>
              )}
            </TableRow>
          </TableHead>

          <TableBody>
            {isLoadingCurrent ? (
              <TableRow>
                <TableCell colSpan={tabValue === 0 ? 8 : tabValue === 1 ? 7 : 6} align="center">
                  <CircularProgress />
                </TableCell>
              </TableRow>
            ) : filteredReports.length > 0 ? (
              filteredReports.map((report, index) => (
                <TableRow key={report.reportId || report.id || index}>
                  <TableCell>{index + 1}</TableCell>
                  <TableCell>{report.studentName || report.student_name || '-'}</TableCell>
                  <TableCell>{report.courseName || '-'}</TableCell>
                  <TableCell>{report.asanaName || report.pranayamaName || report.relaxationName || '-'}</TableCell>
                  <TableCell>{new Date(report.date || report.createdAt).toISOString().split('T')[0] || '-'}</TableCell>

                  {tabValue === 0 && (
                    <>
                      <TableCell>{report.performance || '-'}</TableCell>
                      <TableCell>
                        {report.asanaimage ? (
                          <Box onClick={() => handleImageClick(report)} sx={{ cursor: 'pointer' }}>
                            <AsanaPreview
                              imageUrl={report.asanaimage}
                              landmarks={JSON.parse(report.asanalandmarks || '[]')}
                              size={50}
                            />
                          </Box>
                        ) : (
                          <Typography variant="body2" color="text.secondary">No Image</Typography>
                        )}
                      </TableCell>
                      <TableCell>
                        {report.feedBack == null ? (
                          <Button variant="outlined" size="small" onClick={() => handleOpenFeedbackDialog(report)}>
                            Give Feedback
                          </Button>
                        ) : (
                          <Button
                            variant="contained"
                            size="small"
                            onClick={() => handleViewFeedback(report.feedBack)}
                            sx={{ backgroundColor: '#1976d2', color: '#fff' }}
                          >
                            View Feedback
                          </Button>
                        )}
                      </TableCell>
                    </>
                  )}

                  {tabValue === 1 && (
                    <>
                      <TableCell>{report.totalDuration || '-'}</TableCell>
                      <TableCell>{report.totalLoopsCompleted || '-'}</TableCell>
                      <TableCell>{report.interruptions || '0'}</TableCell>
                    </>
                  )}

                  {tabValue === 2 && (
                    <TableCell align="right">
                      <Chip label="Completed" color="success" size="small" variant="outlined" />
                    </TableCell>
                  )}
                </TableRow>
              ))
            ) : (
              <TableRow>
                <TableCell colSpan={tabValue === 0 ? 8 : tabValue === 1 ? 7 : 6} align="center">
                  <Lottie options={loaderOptions} height="25%" width="25%" />
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>

      {tabValue === 0 && (
        <FeedbackDialogs
          openFeedbackDialog={openFeedbackDialog}
          currentFeedbackRow={currentFeedbackRow}
          feedbackText={feedbackText}
          setFeedbackText={setFeedbackText}
          handleCloseFeedbackDialog={handleCloseFeedbackDialog}
          handleSubmitFeedback={handleSubmitFeedback}
          openViewFeedbackDialog={openViewFeedbackDialog}
          viewFeedbackText={viewFeedbackText}
          handleCloseViewFeedbackDialog={handleCloseViewFeedbackDialog}
        />
      )}
    </MainCard>
  );
}